import { IMask } from "react-imask";

import type {
  DeepPartial,
  RemoveNullAndUndefined,
} from "Shared/Utils/utilTypes";

export const isEmptyObject = (obj: any) => {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
};

export function getNestedProperty(obj: any, path: string) {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

type State = Record<string, any>;

export function updateNestedState(
  state: State,
  keyPath: string,
  value: any,
): State {
  const keys = keyPath.split(".");

  const update = (currentState: State, keys: string[], value: any): State => {
    const [key, ...remainingKeys] = keys;

    if (remainingKeys.length === 0) {
      return { ...currentState, [key]: value };
    }

    return {
      ...currentState,
      [key]: update(currentState[key] || {}, remainingKeys, value),
    };
  };

  return update(state, keys, value);
}

export function removeNullsAndUndefined<T extends Record<string, any>>(
  obj: T,
): DeepPartial<RemoveNullAndUndefined<T>> {
  if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
    throw new Error(
      "removeNullsAndUndefined function expects a non-null object.",
    );
  }

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, value]) => value !== null && value !== undefined)
      .map(([key, value]) =>
        typeof value === "object" &&
        value !== null &&
        value !== undefined &&
        !Array.isArray(value)
          ? [key, removeNullsAndUndefined(value)]
          : [key, value],
      ),
  ) as Partial<T>;
}

export const getFormattedValue = (
  value: any,
  valueFormatter: ((value: any) => string) | undefined,
  mask: InstanceType<typeof IMask.MaskedPattern<any>> | undefined,
) => {
  if (valueFormatter) {
    return valueFormatter(value);
  }

  if (mask && value) {
    mask.resolve(value.toString() || value);
    return mask.value;
  }

  return value;
};

/** Format IBAN for display: uppercase, grouped in blocks of 4. */
export const formatIbanDisplay = (value: string): string => {
  const clean = value.replace(/\s/g, "").toUpperCase();
  return clean.replace(/(.{4})/g, "$1 ").trim();
};

export const phoneMask = IMask.createMask({
  mask: "+NUM",
  thousandsSeparator: "",
  lazy: false,
  blocks: {
    NUM: {
      mask: "000 000 000 000",
      lazy: true,
    },
  },
});
